import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { RotateCcw, Trophy } from "lucide-react";
import { useLang, fill } from "../../i18n/LanguageContext";

const SYMBOLS = ["★", "☾", "♪", "🎭", "🎸", "🥁", "👑", "🎤"];

const build = () =>
  [...SYMBOLS, ...SYMBOLS]
    .map((s, i) => ({ id: i, s, flipped: false, matched: false }))
    .sort(() => Math.random() - 0.5);

export const MemoryMatch = () => {
  const { t } = useLang();
  const s = t.games.memory;
  const [cards, setCards] = useState(build);
  const [picked, setPicked] = useState([]);
  const [moves, setMoves] = useState(0);
  const [lock, setLock] = useState(false);

  const matches = cards.filter((c) => c.matched).length / 2;
  const won = matches === SYMBOLS.length;

  useEffect(() => {
    if (picked.length === 2) {
      setLock(true);
      setMoves((m) => m + 1);
      const [a, b] = picked;
      if (cards[a].s === cards[b].s) {
        setCards((cs) => cs.map((c, i) => (i === a || i === b ? { ...c, matched: true } : c)));
        setPicked([]);
        setLock(false);
      } else {
        const tm = setTimeout(() => {
          setCards((cs) => cs.map((c, i) => (i === a || i === b ? { ...c, flipped: false } : c)));
          setPicked([]);
          setLock(false);
        }, 750);
        return () => clearTimeout(tm);
      }
    }
  }, [picked, cards]);

  const flip = (i) => {
    if (lock || cards[i].flipped || cards[i].matched) return;
    setCards((cs) => cs.map((c, idx) => (idx === i ? { ...c, flipped: true } : c)));
    setPicked((p) => [...p, i]);
  };

  const reset = () => {
    setCards(build());
    setPicked([]);
    setMoves(0);
    setLock(false);
  };

  return (
    <div data-testid="game-memory" className="flex flex-col items-center">
      <div className="mb-4 flex w-full items-center justify-between text-sm text-white/70">
        <span>{s.moves}: <span className="font-display text-lg text-gold-light">{moves}</span></span>
        <span>{s.pairs}: <span className="font-display text-lg text-gold-light">{matches}/{SYMBOLS.length}</span></span>
        <button onClick={reset} data-testid="memory-reset" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-white/70 hover:text-gold-light">
          <RotateCcw className="h-3.5 w-3.5" /> {s.reset}
        </button>
      </div>

      {won && (
        <div className="mb-4 flex items-center gap-2 rounded-full bg-gold-primary/20 px-5 py-2 text-gold-light">
          <Trophy className="h-4 w-4" /> {fill(s.won, { n: moves })}
        </div>
      )}

      <div className="grid grid-cols-4 gap-2.5 sm:gap-3">
        {cards.map((c, i) => (
          <button key={c.id} onClick={() => flip(i)} data-testid={`memory-card-${i}`} className="relative h-16 w-16 sm:h-20 sm:w-20" style={{ perspective: 600 }}>
            <motion.div className="relative h-full w-full" style={{ transformStyle: "preserve-3d" }} animate={{ rotateY: c.flipped || c.matched ? 180 : 0 }} transition={{ duration: 0.4 }}>
              <div className="absolute inset-0 flex items-center justify-center rounded-xl border border-white/10 bg-ink-700 [backface-visibility:hidden]">
                <span className="text-gold-light/40 text-xl">✦</span>
              </div>
              <div className={`absolute inset-0 flex items-center justify-center rounded-xl border [transform:rotateY(180deg)] [backface-visibility:hidden] ${c.matched ? "border-gold-light bg-gold-primary/20" : "border-gold-light/40 bg-ink-600"}`}>
                <span className="text-2xl sm:text-3xl">{c.s}</span>
              </div>
            </motion.div>
          </button>
        ))}
      </div>
    </div>
  );
};
